import { useState } from "react";
import type { SingleValue } from "react-select";

import { SelectInput } from "components/Select";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  IStoreDeposit,
  IStoreDepositAccount,
} from "src/@types/store-deposit-maintenance";

import { formatAmount, formatOperationDate, text } from "../deposit-formatters";
import { DEPOSIT_MAINTENANCE_LABELS, FilterOption } from "../types";

interface UpdateAccountModalProps {
  readonly deposit: IStoreDeposit | null;
  readonly accounts: readonly IStoreDepositAccount[];
  readonly isLoadingAccounts: boolean;
  readonly isSaving: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (accountSequence: number) => void;
}

const toOption = (account: IStoreDepositAccount): FilterOption => ({
  value: String(account.sequence),
  label: `${account.bankName ?? account.bankNumber} (${account.bankNumber}) · Ag. ${account.agency} · Conta ${account.account}`,
});

export function UpdateAccountModal(props: UpdateAccountModalProps) {
  const [selected, setSelected] = useState<FilterOption | null>(null);

  if (props.deposit === null) return null;

  const options: FilterOption[] = props.accounts.map(toOption);

  const handleConfirm = (): void => {
    if (selected === null) return;
    props.onSubmit(Number(selected.value));
  };

  return (
    <Dialog open onOpenChange={props.onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {DEPOSIT_MAINTENANCE_LABELS.editAccountTitle}
          </DialogTitle>
          <DialogDescription>
            {DEPOSIT_MAINTENANCE_LABELS.editAccountDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <dl className="grid grid-cols-2 gap-2 text-xs text-slate-600">
            <div>
              <dt className="text-slate-500">
                {DEPOSIT_MAINTENANCE_LABELS.depositDateLabel}
              </dt>
              <dd>{formatOperationDate(props.deposit.operationDate)}</dd>
            </div>
            <div>
              <dt className="text-slate-500">
                {DEPOSIT_MAINTENANCE_LABELS.depositAmountLabel}
              </dt>
              <dd>{formatAmount(props.deposit.amount)}</dd>
            </div>
          </dl>

          <p className="text-xs text-slate-500">
            {DEPOSIT_MAINTENANCE_LABELS.currentAccountLabel}{" "}
            <span className="font-mono">{text(props.deposit.complement)}</span>
          </p>

          <div>
            <label className="text-xs text-slate-500 mb-1 block">
              {DEPOSIT_MAINTENANCE_LABELS.accountSelectLabel}
            </label>
            <SelectInput
              options={options}
              value={selected}
              onChange={(newValue: unknown) =>
                setSelected((newValue as SingleValue<FilterOption>) ?? null)
              }
              isLoading={props.isLoadingAccounts}
              isDisabled={props.isSaving}
              placeholder={DEPOSIT_MAINTENANCE_LABELS.accountSelectPlaceholder}
            />
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="ghost" onClick={props.onClose}>
            {DEPOSIT_MAINTENANCE_LABELS.cancelButton}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selected === null || props.isSaving}
            className="bg-slate-800 hover:bg-slate-900 text-white"
          >
            {props.isSaving
              ? DEPOSIT_MAINTENANCE_LABELS.savingButton
              : DEPOSIT_MAINTENANCE_LABELS.confirmButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
